import { useRef, useEffect, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

const HeroSection = () => {
  const { t } = useLanguage();
  const containerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });

  const contentY = useTransform(scrollYProgress, [0, 1], [0, 150]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  useEffect(() => {
    setTimeout(() => setIsVisible(true), 300);
  }, []);

  return (
    <section 
      ref={containerRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <motion.img
          src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=2400&q=90"
          alt="Palm trees city skyline"
          className="w-full h-full object-cover"
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.5, ease: "easeOut" }}
        />
        {/* Dark overlay for text readability */}
        <div 
          className="absolute inset-0"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.45)' }}
        />
      </div>

      {/* Content */}
      <motion.div 
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 container-custom text-center text-white px-6"
      >
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <motion.span
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            className="inline-block overline text-white mb-8 px-8 py-3 border border-white/40 rounded-full backdrop-blur-md bg-white/10"
            data-testid="hero-label"
          >
            {t('hero.label')}
          </motion.span>
        </motion.div>

        {/* Main Title */}
        <motion.h1
          initial={{ opacity: 0, y: 60 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1, delay: 0.4 }}
          className="font-serif text-6xl md:text-8xl lg:text-9xl font-bold mb-8 tracking-tight"
          style={{
            textShadow: '0 4px 30px rgba(0,0,0,0.5)',
          }}
          data-testid="hero-title"
        >
          BAO AGENCY
        </motion.h1>

        {/* Description */}
        <motion.p
          initial={{ opacity: 0, y: 40 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="max-w-3xl mx-auto text-lg md:text-xl leading-relaxed mb-12 text-white/95"
          style={{ textShadow: '0 2px 15px rgba(0,0,0,0.4)' }}
          data-testid="hero-description"
        >
          {t('hero.description')}
        </motion.p>

        {/* CTA Button */}
        <motion.a
          href="#about"
          initial={{ opacity: 0, y: 30, scale: 0.9 }}
          animate={isVisible ? { opacity: 1, y: 0, scale: 1 } : {}}
          whileHover={{ 
            scale: 1.08, 
            boxShadow: '0 25px 50px rgba(255,255,255,0.3)',
            y: -3,
          }}
          whileTap={{ scale: 0.95 }}
          transition={{ 
            duration: 0.6, 
            delay: 0.8,
            type: "spring",
            stiffness: 200,
          }}
          className="inline-block btn-pill bg-white text-black font-bold px-12 py-5 text-lg relative overflow-hidden group"
          data-testid="hero-cta"
        >
          <motion.span
            className="absolute inset-0 bg-gradient-to-r from-neutral-200 to-neutral-100 opacity-0 group-hover:opacity-100"
            initial={{ x: '-100%' }}
            whileHover={{ x: '0%' }}
            transition={{ duration: 0.3 }}
          />
          <span className="relative z-10">{t('hero.cta')}</span>
        </motion.a>
      </motion.div>
    </section>
  );
};

export default HeroSection;
